/**
 * Dashboard navigation configuration.
 *
 * ╔═══════════════════════════════════════════════════════════════════╗
 * ║  HOW TO ADD A NEW DASHBOARD SECTION (e.g. "Markets")             ║
 * ║  ─────────────────────────────────────────────────────────────── ║
 * ║  1. Add ONE entry to the `navItems` array below — a leaf         ║
 * ║     `NavItem` for a single page, or a `NavGroup` if the section  ║
 * ║     has multiple sub-pages that should collapse under one        ║
 * ║     sidebar header (see the "Markets" group below).              ║
 * ║  2. Create the page component in                                 ║
 * ║       src/components/dashboard/pages/<YourPage>.tsx              ║
 * ║  3. Add ONE <Route> child in App.tsx under the /dashboard parent  ║
 * ║     for every leaf path (a NavGroup contributes one Route per    ║
 * ║     child, not one for the group itself — the group has no page).║
 * ║  The sidebar and navigation auto-render the new link/group.      ║
 * ╚═══════════════════════════════════════════════════════════════════╝
 */

/** A single, directly-navigable sidebar link. */
export interface NavItem {
  /** Absolute path used for NavLink `to` prop and Route `path`. */
  path: string
  /** Sidebar label text. */
  label: string
  /** Material Icons ligature name (font-family: 'Material Icons'). */
  materialIcon: string
  /** When true, only renders for users with role === 'admin'. */
  adminOnly?: boolean
}

/**
 * A collapsible sidebar group with no page of its own — it renders a
 * toggleable header plus a list of `NavItem` children. The group
 * auto-expands when the current route matches one of its children, and can
 * otherwise be toggled open/closed by clicking the header (session-only,
 * not persisted).
 */
export interface NavGroup {
  /** Sidebar label text for the group header. */
  label: string
  /** Material Icons ligature name for the group header. */
  materialIcon: string
  /** Leaf pages rendered under this group when expanded. */
  children: NavItem[]
  /** When true, the entire group (and all children) is hidden for non-admins. */
  adminOnly?: boolean
}

/** Union of everything that can appear in the top-level `navItems` array. */
export type NavEntry = NavItem | NavGroup

/** Narrows a `NavEntry` to `NavGroup` — groups are the only entries with `children`. */
export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry
}

export const navItems: NavEntry[] = [
  {
    path: '/dashboard',
    label: 'Overview',
    materialIcon: 'dashboard',
  },
  {
    path: '/dashboard/images',
    label: 'Images',
    materialIcon: 'image',
  },
  {
    path: '/dashboard/audio',
    label: 'Audio',
    materialIcon: 'audio_file',
  },
  {
    path: '/dashboard/admin/users',
    label: 'User Management',
    materialIcon: 'manage_accounts',
    adminOnly: true,
  },
  {
    label: 'Markets',
    materialIcon: 'show_chart',
    children: [
      {
        path: '/dashboard/markets',
        label: 'Market Overview',
        materialIcon: 'insights',
      },
      {
        path: '/dashboard/markets/stock-quote',
        label: 'Stock Quote',
        materialIcon: 'query_stats',
      },
      {
        path: '/dashboard/markets/movers',
        label: 'Movers',
        materialIcon: 'trending_up',
      },
      {
        path: '/dashboard/options-calculator',
        label: 'Options Calculator',
        materialIcon: 'calculate',
      },
    ],
  },
  {
    label: 'Invoicing',
    materialIcon: 'receipt_long',
    adminOnly: true,
    children: [
      {
        path: '/dashboard/invoicing',
        label: 'Invoices',
        materialIcon: 'request_quote',
      },
      {
        path: '/dashboard/invoicing/clients',
        label: 'Clients',
        materialIcon: 'contacts',
      },
      {
        path: '/dashboard/invoicing/settings',
        label: 'Settings',
        materialIcon: 'settings',
      },
    ],
  },
  // ── Future sections go here ─────────────────────────────────────────
]
